import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { flightManualCheckoutService } from '../services/flightManualCheckoutService';
import { DateTimeInput, DateInput } from '../components';
import styles from './QuotePage.module.css';

// ─── tipos locais ─────────────────────────────────────────────────────────────

interface PassengerForm {
  firstName: string;
  lastName: string;
  documentType: 'CPF' | 'PASSPORT';
  documentNumber: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  gender: 'male' | 'female';
}

interface StopForm {
  stopOrder: string;
  isEscale: boolean;
  flightNumber: string;
  departureLocation: string;
  arrivalLocation: string;
  departureTime: string;
  arrivalTime: string;
  flightDuration: string;
  timeWaiting: string;
  companyCode: string;
  cabin: string;
}

interface SegmentForm {
  flightNumber: string;
  airlineName: string;
  airlineCode: string;
  departureLocation: string;
  arrivalLocation: string;
  departureTime: string;
  arrivalTime: string;
  flightDuration: string;
  cabin: string;
  stops: StopForm[];
}

// ─── empties ──────────────────────────────────────────────────────────────────

const emptyPassenger = (): PassengerForm => ({
  firstName: '', lastName: '', documentType: 'CPF',
  documentNumber: '', birthDay: '', birthMonth: '', birthYear: '', gender: 'male',
});

const emptyStop = (): StopForm => ({
  stopOrder: '', isEscale: false, flightNumber: '',
  departureLocation: '', arrivalLocation: '',
  departureTime: '', arrivalTime: '',
  flightDuration: '', timeWaiting: '', companyCode: '', cabin: '',
});

const emptySegment = (): SegmentForm => ({
  flightNumber: '', airlineName: '', airlineCode: '',
  departureLocation: '', arrivalLocation: '',
  departureTime: '', arrivalTime: '',
  flightDuration: '', cabin: '', stops: [],
});

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtPrice = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function buildSegment(form: SegmentForm) {
  const uniqueId = `manual-${form.flightNumber}-${Date.now()}`;
  return {
    uniqueId,
    flightNumber: form.flightNumber,
    airlineName: form.airlineName,
    airlineCode: form.airlineCode || undefined,
    departureLocation: form.departureLocation,
    arrivalLocation: form.arrivalLocation,
    departureTime: form.departureTime,
    arrivalTime: form.arrivalTime,
    flightDuration: form.flightDuration,
    ourPrice: 0,
    tax: 0,
    qntStop: form.stops.length,
    cabin: form.cabin || undefined,
    stops: form.stops.map((s, i) => ({
      stopOrder: Number(s.stopOrder) || i + 1,
      op: s.isEscale ? 'escale' : 'connection',
      isEscale: s.isEscale,
      flightNumber: s.flightNumber,
      departureTime: s.departureTime,
      arrivalTime: s.arrivalTime,
      departureLocation: s.departureLocation,
      arrivalLocation: s.arrivalLocation,
      flightDuration: s.flightDuration || '',
      timeWaiting: s.timeWaiting || undefined,
      companyCode: s.companyCode || undefined,
      cabin: s.cabin || undefined,
      qntEscale: 0,
    })),
  };
}

// ─── sub-componentes ──────────────────────────────────────────────────────────

const StopEditor: React.FC<{
  stops: StopForm[];
  onChange: (stops: StopForm[]) => void;
}> = ({ stops, onChange }) => {
  const update = (i: number, field: keyof StopForm, value: string | boolean) =>
    onChange(stops.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  return (
    <>
      {stops.map((s, i) => (
        <div key={i} className={styles.passengerCard}>
          <p className={styles.passengerTitle}>
            Parada / Escala {i + 1}
            <button type="button" onClick={() => onChange(stops.filter((_, idx) => idx !== i))}
              style={{ marginLeft: 12, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
              ✕ Remover
            </button>
          </p>
          <div className={styles.formRow}>
            <label className={styles.checkRow} style={{ marginBottom: 0 }}>
              <input type="checkbox" checked={s.isEscale}
                onChange={e => update(i, 'isEscale', e.target.checked)} />
              É escala (sem troca de avião)
            </label>
          </div>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Nº do Voo</label>
              <input value={s.flightNumber} onChange={e => update(i, 'flightNumber', e.target.value)} placeholder="LA3042" />
            </div>
            <div className={styles.inputGroup}>
              <label>Código Cia</label>
              <input value={s.companyCode} onChange={e => update(i, 'companyCode', e.target.value)} placeholder="LA" />
            </div>
            <div className={styles.inputGroup}>
              <label>Cabine</label>
              <input value={s.cabin} onChange={e => update(i, 'cabin', e.target.value)} placeholder="Y" />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Origem</label>
              <input value={s.departureLocation} onChange={e => update(i, 'departureLocation', e.target.value)} placeholder="GRU" />
            </div>
            <div className={styles.inputGroup}>
              <label>Destino</label>
              <input value={s.arrivalLocation} onChange={e => update(i, 'arrivalLocation', e.target.value)} placeholder="CGH" />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Partida</label>
              <DateTimeInput value={s.departureTime} onChange={v => update(i, 'departureTime', v)} />
            </div>
            <div className={styles.inputGroup}>
              <label>Chegada</label>
              <DateTimeInput value={s.arrivalTime} onChange={v => update(i, 'arrivalTime', v)} />
            </div>
            <div className={styles.inputGroup}>
              <label>Duração do Voo</label>
              <input value={s.flightDuration} onChange={e => update(i, 'flightDuration', e.target.value)} placeholder="1h30" />
            </div>
            <div className={styles.inputGroup}>
              <label>Tempo de Espera</label>
              <input value={s.timeWaiting} onChange={e => update(i, 'timeWaiting', e.target.value)} placeholder="2h00" />
            </div>
          </div>
        </div>
      ))}
      <button type="button" className={styles.btnSecondary}
        style={{ height: 36, padding: '0 14px', fontSize: 13 }}
        onClick={() => onChange([...stops, emptyStop()])}>
        + Adicionar Parada / Escala
      </button>
    </>
  );
};

const SegmentEditor: React.FC<{
  label: string;
  seg: SegmentForm;
  onChange: (s: SegmentForm) => void;
}> = ({ label, seg, onChange }) => {
  const upd = (field: keyof SegmentForm, value: string) =>
    onChange({ ...seg, [field]: value });

  return (
    <div className={styles.card}>
      <h2 className={styles.sectionTitle}>{label}</h2>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Nº do Voo</label>
          <input value={seg.flightNumber} required onChange={e => upd('flightNumber', e.target.value)} placeholder="G31234" />
        </div>
        <div className={styles.inputGroup}>
          <label>Companhia Aérea</label>
          <input value={seg.airlineName} required onChange={e => upd('airlineName', e.target.value)} placeholder="GOL" />
        </div>
        <div className={styles.inputGroup} style={{ flex: '0 1 110px' }}>
          <label>Código IATA</label>
          <input value={seg.airlineCode} onChange={e => upd('airlineCode', e.target.value)} placeholder="G3" />
        </div>
        <div className={styles.inputGroup} style={{ flex: '0 1 110px' }}>
          <label>Cabine</label>
          <input value={seg.cabin} onChange={e => upd('cabin', e.target.value)} placeholder="Y" />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Aeroporto Origem</label>
          <input value={seg.departureLocation} required onChange={e => upd('departureLocation', e.target.value)} placeholder="GRU" />
        </div>
        <div className={styles.inputGroup}>
          <label>Aeroporto Destino</label>
          <input value={seg.arrivalLocation} required onChange={e => upd('arrivalLocation', e.target.value)} placeholder="SSA" />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Partida</label>
          <DateTimeInput value={seg.departureTime} onChange={v => upd('departureTime', v)} required />
        </div>
        <div className={styles.inputGroup}>
          <label>Chegada</label>
          <DateTimeInput value={seg.arrivalTime} onChange={v => upd('arrivalTime', v)} required />
        </div>
        <div className={styles.inputGroup}>
          <label>Duração Total</label>
          <input value={seg.flightDuration} required onChange={e => upd('flightDuration', e.target.value)} placeholder="2h15" />
        </div>
      </div>

      <p className={styles.flightGroupTitle} style={{ marginTop: 8 }}>Paradas / Escalas</p>
      <StopEditor stops={seg.stops} onChange={stops => onChange({ ...seg, stops })} />
    </div>
  );
};

// ─── página principal ─────────────────────────────────────────────────────────

const FlightManualCheckout: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // cliente
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'credit_card' | 'debit_card' | 'kairu' | 'zartoes'>('pix');

  // viagem
  const [roundTrip, setRoundTrip] = useState(false);
  const [flightDate, setFlightDate] = useState('');
  const [returnDate, setReturnDate] = useState('');

  // segmentos
  const [outbound, setOutbound] = useState<SegmentForm>(emptySegment());
  const [inbound, setInbound] = useState<SegmentForm>(emptySegment());

  // passageiros
  const [passengers, setPassengers] = useState<PassengerForm[]>([emptyPassenger()]);

  // preços
  const [basePrice, setBasePrice] = useState('');
  const [taxes, setTaxes] = useState('');

  const total = (Number(basePrice) || 0) + (Number(taxes) || 0);

  // estado de envio
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const updatePax = (i: number, field: keyof PassengerForm, value: string) =>
    setPassengers(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitting(true);

    const rawPhone = phone.replace(/\D/g, '');
    const areaCode = rawPhone.length >= 10 ? rawPhone.slice(0, 2) : '31';
    const phoneNumber = rawPhone.length >= 10 ? rawPhone.slice(2) : rawPhone;

    const route = `${outbound.departureLocation}-${outbound.arrivalLocation}`;
    const outboundSegment = buildSegment(outbound);
    const inboundSegment = roundTrip ? buildSegment(inbound) : undefined;

    try {
      await flightManualCheckoutService.createManualCheckout({
        notes: notes || `Checkout manual aéreo — operador: ${user?.username ?? ''}`,
        passengers: passengers.map(p => ({
          firstName: p.firstName,
          lastName: p.lastName,
          documentType: p.documentType,
          documentNumber: p.documentNumber,
          birthDay: p.birthDay,
          birthMonth: p.birthMonth,
          birthYear: p.birthYear,
          gender: p.gender,
          country: 'Brasil',
        })),
        contact: {
          email,
          confirmEmail: email,
          areaCode,
          phoneNumber,
        },
        payment: { type: paymentMethod },
        invoice: { type: 'individual' },
        flight: {
          tripType: roundTrip ? 'round_trip' : 'one_way',
          outboundFlightId: outboundSegment.uniqueId,
          outboundFareId: outboundSegment.uniqueId,
          returnFlightId: inboundSegment?.uniqueId,
          returnFareId: inboundSegment?.uniqueId,
          route,
          date: flightDate,
          returnDate: roundTrip ? returnDate : undefined,
          passengers: passengers.length,
          airline: outbound.airlineName,
          duration: outbound.flightDuration,
          departureTime: outbound.departureTime,
          arrivalTime: outbound.arrivalTime,
          departureLocation: outbound.departureLocation,
          arrivalLocation: outbound.arrivalLocation,
          returnDepartureTime: inbound.departureTime || undefined,
          returnArrivalTime: inbound.arrivalTime || undefined,
          outboundFlight: outboundSegment,
          returnFlight: inboundSegment,
        },
        pricing: {
          basePrice: Number(basePrice) || 0,
          taxes: Number(taxes) || 0,
          total,
        },
      });

      setSuccess('Checkout de aéreo criado com sucesso!');
      setTimeout(() => navigate('/emissions'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar checkout manual');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Checkout Manual de Aéreo</h1>

      {/* ── abas ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button type="button" className={styles.btnSecondary} style={{ height: 40, padding: '0 20px' }}
          onClick={() => navigate('/quotes')}>
          🔍 Buscar via API
        </button>
        <button type="button" className={styles.btnPrimary} style={{ height: 40, padding: '0 20px' }}>
          ✏️ Entrada Manual
        </button>
      </div>

      <form onSubmit={handleSubmit}>

        {/* 1. Cliente */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>1. Dados do Cliente</h2>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Email</label>
              <input type="email" value={email} required onChange={e => setEmail(e.target.value)} />
            </div>
            <div className={styles.inputGroup}>
              <label>Telefone</label>
              <input value={phone} required onChange={e => setPhone(e.target.value)} placeholder="31999999999" />
            </div>
            <div className={styles.inputGroup} style={{ flex: '0 1 200px' }}>
              <label>Método de Pagamento</label>
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as any)}>
                <option value="pix">PIX</option>
                <option value="credit_card">Cartão de Crédito</option>
                <option value="debit_card">Cartão de Débito</option>
                <option value="kairu">Kairu</option>
                <option value="zartoes">2 Cartões</option>
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Observação</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Nota interna do operador" />
            </div>
          </div>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={roundTrip} onChange={e => setRoundTrip(e.target.checked)} />
            Ida e Volta
          </label>
        </div>

        {/* 2. Datas */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>2. Datas da Viagem</h2>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Data de Ida</label>
              <DateInput value={flightDate} onChange={v => setFlightDate(v)} required />
            </div>
            {roundTrip && (
              <div className={styles.inputGroup}>
                <label>Data de Volta</label>
                <DateInput value={returnDate} onChange={v => setReturnDate(v)} required={roundTrip} />
              </div>
            )}
          </div>
        </div>

        {/* 3. Voo de Ida */}
        <SegmentEditor
          label={`3. Voo de Ida`}
          seg={outbound}
          onChange={setOutbound}
        />

        {/* 4. Voo de Volta */}
        {roundTrip && (
          <SegmentEditor
            label="4. Voo de Volta"
            seg={inbound}
            onChange={setInbound}
          />
        )}

        {/* Passageiros */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>{roundTrip ? '5' : '4'}. Passageiros</h2>
          {passengers.map((p, i) => (
            <div key={i} className={styles.passengerCard}>
              <p className={styles.passengerTitle}>
                Passageiro {i + 1}
                {passengers.length > 1 && (
                  <button type="button"
                    onClick={() => setPassengers(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ marginLeft: 12, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                    ✕ Remover
                  </button>
                )}
              </p>
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label>Nome</label>
                  <input value={p.firstName} required onChange={e => updatePax(i, 'firstName', e.target.value)} />
                </div>
                <div className={styles.inputGroup}>
                  <label>Sobrenome</label>
                  <input value={p.lastName} required onChange={e => updatePax(i, 'lastName', e.target.value)} />
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 1 140px' }}>
                  <label>Gênero</label>
                  <select value={p.gender} onChange={e => updatePax(i, 'gender', e.target.value)}>
                    <option value="male">Masculino</option>
                    <option value="female">Feminino</option>
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.inputGroup} style={{ flex: '0 1 140px' }}>
                  <label>Documento</label>
                  <select value={p.documentType} onChange={e => updatePax(i, 'documentType', e.target.value)}>
                    <option value="CPF">CPF</option>
                    <option value="PASSPORT">Passaporte</option>
                  </select>
                </div>
                <div className={styles.inputGroup}>
                  <label>Número</label>
                  <input value={p.documentNumber} required onChange={e => updatePax(i, 'documentNumber', e.target.value)} />
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 1 auto' }}>
                  <label>Nascimento</label>
                  <div className={styles.birthRow}>
                    <div className={styles.inputGroup} style={{ flex: '0 1 70px' }}>
                      <label>Dia</label>
                      <input placeholder="DD" maxLength={2} value={p.birthDay} required onChange={e => updatePax(i, 'birthDay', e.target.value)} />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 1 70px' }}>
                      <label>Mês</label>
                      <input placeholder="MM" maxLength={2} value={p.birthMonth} required onChange={e => updatePax(i, 'birthMonth', e.target.value)} />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 1 90px' }}>
                      <label>Ano</label>
                      <input placeholder="AAAA" maxLength={4} value={p.birthYear} required onChange={e => updatePax(i, 'birthYear', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button type="button" className={styles.btnSecondary}
            onClick={() => setPassengers(prev => [...prev, emptyPassenger()])}>
            + Adicionar Passageiro
          </button>
        </div>

        {/* Preços */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>{roundTrip ? '6' : '5'}. Preços</h2>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Preço Base (R$)</label>
              <input type="number" step="0.01" min="0" value={basePrice} required
                onChange={e => setBasePrice(e.target.value)} placeholder="0.00" />
            </div>
            <div className={styles.inputGroup}>
              <label>Taxas (R$)</label>
              <input type="number" step="0.01" min="0" value={taxes} required
                onChange={e => setTaxes(e.target.value)} placeholder="0.00" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <p className={styles.summaryTotal} style={{ margin: 0 }}>
                Total: {fmtPrice(total)}
              </p>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className={styles.btnRow}>
          <button type="submit" className={styles.btnPrimary} disabled={submitting}>
            {submitting ? 'Criando...' : '✈️ Criar Checkout Manual'}
          </button>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {success && (
          <div className={styles.linkBox}>
            <p className={styles.linkBoxTitle}>✅ {success}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default FlightManualCheckout;
